import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useFlightFareFamilies from 'hooks/useFlightFareFamilies'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { arrayToMap, last, sortBy } from 'lib/array/arrayUtils'
import FareFamilyFareOption from './FareFamilyFareOption'
import FareFamilySelectFareSkeleton from './FareFamilyFareOptionSkeleton'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import HelpPane from 'components/Common/HelpPane/HelpPane'
import useToggle from 'hooks/useToggle'
import FlightFareRulesModalContent from 'components/Common/Modals/FlightFareRulesModal/FlightFareRulesModalContent'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import FareFamilySelectModalReturnFareHeaderItem from './FareFamilySelectModalReturnFareHeaderItem'
import { FlightViewTypes } from 'constants/flight'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import FareFamilySelectModalSingleRouteFareHeaderItem from './FareFamilySelectModalSingleRouteFareHeaderIte'
import { getFeaturesByFareFamily, isFlightJourneyDomestic } from 'lib/flights/flightUtils'
import CFMRUnforeseenEventsModalContent from './CFMRUnforeseenEventsModalContent'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { restoreIdsOfInsuranceFareFamily } from 'lib/insurance/insuranceFlights'
import useModalCloseOnLocationChange from 'hooks/Modal/useModalCloseOnLocationChange'
import FareFamilyModalDisclaimer, { FareFamilyDisclaimerContainer } from './FareFamilyModalDisclaimer'
import { useIntl } from 'react-intl'
import getFareFamilyFeederBudgetLabels, { FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE } from 'lib/flights/fareFamilyFeederBudgetLabels'

const ErrorPane = styled(Pane)`
  padding: ${rem(24)};
`
interface Props {
  searchId: string
  sessionId?: string
  occupancies: App.Occupants
  journey: App.JourneyV2
  departingJourney?: App.JourneyV2
  departingFareFamily?: App.FlightFareFamily
  relativePrice?: number
  isStandalone?: boolean
  isMultiCity?: boolean
  isLastSelection?: boolean
  canAddToUserCart?: boolean
  flightItem?: App.Checkout.FlightItem
  /*
   * Pre-fetched fare families to be sent in the useFlightFareFamilies hook
   * to avoid fetching them again from the server
   * Used for date change scenarios where we already have the upsell data
   * fetched from the server in the change date search response
   */
  cachedFareFamilies?: App.FlightsState['fareFamiliesByFlight'][0]
}

function FareFamilySelectModal({
  journey,
  searchId,
  sessionId,
  occupancies,
  departingFareFamily,
  departingJourney,
  relativePrice,
  isStandalone,
  canAddToUserCart,
  isLastSelection,
  flightItem,
  cachedFareFamilies,
}: Props) {
  const intl = useIntl()
  const isCroFlexibleFlightFaresEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croFlexibleFlightFares)
  const [fareRules, setFareRules] = useState<string | undefined>()
  const [showCfmrUnforeseenEvents, setShowCfmrUnforeseenEvents] = useState(false)
  const [cfmrUnforeseenEventsCost, setCfmrUnforeseenEventsCost] = useState<number | undefined>()
  const [showAllFeatures, toggleShowAllFeatures] = useToggle(false)
  const modal = useModalElementContext<{ fareFamily: App.FlightFareFamily, returningFareFamily?: App.FlightFareFamily, addToUserCart?: boolean, points?: App.LoyaltyPointsEarnCalculation, fareFamiliesKey?: string } | undefined>()

  useModalCloseOnLocationChange()

  const firstFlight = journey.flightGroup.flights[0]
  const lastFlight = last(journey.flightGroup.flights)
  const isReturnType = journey.fareType === FlightViewTypes.RETURN
  const isCombinedUpsell = false
  const insuranceDestinationCountry = isReturnType ? last(departingJourney?.flightGroup.flights ?? [])?.arrivalCountry : lastFlight.arrivalCountry
  const insuranceTripStartDate = isReturnType ? departingJourney?.flightGroup.flights[0].departingDate : firstFlight.departingDate
  const insuranceTripEndDate = lastFlight.arrivalDate

  // Use pre-fetched fare families if provided, otherwise fetch via hook
  const { families: fareFamilies, fetching, error, fareFamiliesKey, insuranceQuotes } = useFlightFareFamilies({
    searchId,
    sessionId: sessionId ?? '',
    occupancies,
    provider: journey.provider,
    originAirportCode: firstFlight.departureAirport,
    destinationAirportCode: lastFlight.arrivalAirport,
    destinationCountry: insuranceDestinationCountry,
    departingDate: insuranceTripStartDate,
    arrivalDate: insuranceTripEndDate, // Will be the last flight's arrival date for both One-way and Return Fares
    journeyId: journey.id,
    departingJourneyId: departingJourney?.id,
    isStandalone,
    isReturnType,
    isCombinedUpsell,
    departingFareFamily,
    flightItem,
    // Skip fetching if we have pre-fetched data
    cachedFareFamilies,
  })

  const featuresByFareFamily = useMemo(() => getFeaturesByFareFamily(fareFamilies), [fareFamilies])

  const fareFamiliesById = useMemo(() => {
    return arrayToMap(
      fareFamilies,
      family => family.id,
      family => family,
    )
  }, [fareFamilies])

  const sortedFamilies = useMemo(() => {
    if (!isReturnType) {
      return sortBy(
        fareFamilies,
        fare => fare.costWithInsurance?.all ?? fare.cost.all,
        'asc',
      )
    }

    const result: Array<App.FlightFareFamily> = []
    for (const family of fareFamilies) {
      if (!family.pairedUpsellIds) {
        continue
      }

      for (const pairedId of family.pairedUpsellIds) {
        const pairedFamily = fareFamiliesById.get(pairedId)
        if (!pairedFamily) {
          continue
        }

        /**
         * If the paired family is already in the result, we don't need to add it again
         */
        if (result.find(fare => fare.id === pairedFamily.id && !family.withEmbeddedInsurance)) {
          continue
        }

        result.push({
          ...family,
          cost: {
            all: (family.cost.all) + (pairedFamily.cost.all ?? 0),
            adult: family.cost.adult + (pairedFamily.cost.adult ?? 0),
            children: family.cost.children + (pairedFamily.cost.children ?? 0),
            infant: family.cost.infant + (pairedFamily.cost.infant ?? 0),
          },
          ...(
            family.regularCost && pairedFamily.regularCost ? {
              regularCost: {
                all: family.regularCost.all + pairedFamily.regularCost.all,
                adult: family.regularCost.adult + pairedFamily.regularCost.adult,
                children: family.regularCost.children + pairedFamily.regularCost.children,
                infant: family.regularCost.infant + pairedFamily.regularCost.infant,
              },
            } : {}
          ),
          ...(
            family.withEmbeddedInsurance ? {
              costWithInsurance: {
                all: (family.costWithInsurance?.all ?? 0) + (pairedFamily.costWithInsurance?.all ?? 0),
                adult: (family.costWithInsurance?.adult ?? 0) + (pairedFamily.costWithInsurance?.adult ?? 0),
                children: (family.costWithInsurance?.children ?? 0) + (pairedFamily.costWithInsurance?.children ?? 0),
                infant: (family.costWithInsurance?.infant ?? 0) + (pairedFamily.costWithInsurance?.infant ?? 0),
              },
            } : {}
          ),
        })
      }
    }
    return sortBy(
      result,
      fare => fare.costWithInsurance?.all ?? fare.cost.all,
      'asc',
    )
  }, [fareFamilies, fareFamiliesById, isReturnType])

  const containsProtectedFareFamily = useMemo(() => sortedFamilies.some(fareFamily => fareFamily.withEmbeddedInsurance), [sortedFamilies])

  const modalRef = useRef<HTMLDivElement>(null)
  const onHideFareRules = useCallback(() => {
    setFareRules(undefined)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const onShowFareRules = useCallback((url: string) => {
    setFareRules(url)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const onHideCfmrUnforeseenEvents = useCallback(() => {
    setShowCfmrUnforeseenEvents(false)
    setCfmrUnforeseenEventsCost(undefined)
    modalRef.current?.scrollTo({ top: 0 })
  }, [])

  const onShowCfmrUnforeseenEvents = useCallback((cost: number) => {
    setFareRules(undefined)
    setShowCfmrUnforeseenEvents(true)
    setCfmrUnforeseenEventsCost(cost)
  }, [])

  const fareRuleTitle = intl.formatMessage({
    id: '3vt1eJJoNN4iANV6CUn6',
    defaultMessage: 'Fare rule',
    description: 'Modal title when viewing fare rules in the flights section',
  })
  const refundableTitle = intl.formatMessage({
    id: 'g0FS7g6K21mGdbERnc-X',
    defaultMessage: 'Make your flight fully refundable',
    description: 'Modal title for flight refund insurance option on the form',
  })
  const selectFareTitle = intl.formatMessage({
    id: 'G0WGd3zx0N0QHD9VdUPi',
    defaultMessage: 'Select a fare',
    description: 'Modal title for fare family selection in the flights section',
  })
  const cfmrSubtitle = intl.formatMessage({
    id: 'cfM574oiyll_VZO5grea',
    defaultMessage: 'This exclusive protection from Luxury Escapes and XCover ensures a hassle-free 100% refund if you need to cancel for unforeseen events.',
    description: 'Subtitle explaining the cancellation protection coverage from Luxury Escapes and XCover',
  })
  const title = fareRules ? fareRuleTitle : showCfmrUnforeseenEvents ? refundableTitle : selectFareTitle

  const [fareFamilySelected, setFareFamilySelected] = useState<{ fareFamily?: App.FlightFareFamily, points?: App.LoyaltyPointsEarnCalculation }>({})

  useEffect(() => {
    if (!fareFamilySelected && sortedFamilies.length > 0) {
      setFareFamilySelected({ points: undefined, fareFamily: sortedFamilies[0] })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedFamilies])

  const onConfirmSelection = useCallback((addToUserCart?: boolean) => {
    if (!fareFamilySelected.fareFamily) return

    // The fareFamilySelected.fareFamily contains a fare family with modified cost values for Return Fares.
    // We need to get the original fare family from the fareFamiliesById map.
    const originalFareFamily = fareFamiliesById.get(fareFamilySelected.fareFamily.id)
    if (!originalFareFamily) return

    let returningFareFamily = fareFamilySelected.fareFamily.pairedUpsellIds ? fareFamiliesById.get(fareFamilySelected.fareFamily.pairedUpsellIds[0]) : undefined

    if (returningFareFamily && originalFareFamily.withEmbeddedInsurance) {
      returningFareFamily = {
        ...returningFareFamily,
        withEmbeddedInsurance: true,
      }
    }

    // We need to restore the original IDs since the '-insurance' suffix was only used
    // to differentiate between the original fare and the insurance-included version in the UI.
    // The booking flow requires the original IDs as they map to the actual fare orders.
    const sanitizedReturningFareFamily = returningFareFamily ? restoreIdsOfInsuranceFareFamily(returningFareFamily) : undefined
    const sanitizedOriginalFareFamily = restoreIdsOfInsuranceFareFamily(originalFareFamily)

    modal.resolve({
      fareFamily: sanitizedOriginalFareFamily,
      returningFareFamily: sanitizedReturningFareFamily,
      addToUserCart,
      points: fareFamilySelected.points,
      fareFamiliesKey,
    })
  }, [fareFamiliesById, fareFamilySelected, modal, fareFamiliesKey])

  const secondaryModalProps: ModalFooterActionProps | undefined = useMemo(() => {
    if (isCroFlexibleFlightFaresEnabled) {
      return
    }

    if (canAddToUserCart && isLastSelection) {
      return {
        disabled: !!(fetching || error),
        startIcon: <LineShoppingCartIcon />,
        onClick: () => onConfirmSelection(true),
        children: intl.formatMessage({
          id: 'etljAxHlw2u1hgtx50-K',
          defaultMessage: 'Add to cart',
          description: 'Action button to add flight to cart in fare select modal',
        }),
      }
    }
  }, [isCroFlexibleFlightFaresEnabled, canAddToUserCart, isLastSelection, fetching, error, onConfirmSelection, intl])

  const primaryModalProps: ModalFooterActionProps | undefined = useMemo(() => {
    return {
      disabled: !!(fetching || error || fareFamilySelected.fareFamily === undefined),
      onClick: () => onConfirmSelection(),
      children: isLastSelection && canAddToUserCart ? intl.formatMessage({
        id: 'VAGQJRpDlMWrvpU7VZUd',
        defaultMessage: 'Book now',
        description: 'Button to proceed with booking in fare select modal',
      }) : intl.formatMessage({
        id: '2DKXKj_vCCGYVODqxJmD',
        defaultMessage: 'Select fare',
        description: 'Action button to confirm fare selection in the flights section',
      }),
      'data-testid': 'fare-family-select-modal-primary-action',
    }
  }, [canAddToUserCart, error, fetching, isLastSelection, onConfirmSelection, fareFamilySelected.fareFamily, intl])

  const isDomestic = useMemo(() => {
    return isFlightJourneyDomestic(journey.flightGroup.flights)
  }, [journey])

  const feederBudgetLabelByFareFamilyId = useMemo(() => getFareFamilyFeederBudgetLabels({
    mode: FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE,
    bundleBudget: flightItem?.bundleBudget,
    occupancies,
    fareFamilies: sortedFamilies,
    isReturnType,
    departingJourney,
  }), [flightItem?.bundleBudget, occupancies, sortedFamilies, isReturnType, departingJourney])

  const onCloseButtonClick = useCallback(() => {
    if (fareRules) return onHideFareRules()
    if (showCfmrUnforeseenEvents) return onHideCfmrUnforeseenEvents()
    return modal.resolve(undefined)
  }, [fareRules, modal, onHideCfmrUnforeseenEvents, onHideFareRules, showCfmrUnforeseenEvents])

  const isFareFamilyOptionSelected = useCallback((fareFamily: App.FlightFareFamily) => {
    if (fareFamily.id !== fareFamilySelected.fareFamily?.id) return false

    return fareFamily.withEmbeddedInsurance === fareFamilySelected.fareFamily?.withEmbeddedInsurance
  }, [fareFamilySelected.fareFamily])

  const modalSize = useMemo(() => {
    if (fareRules) return 'L'
    if (showCfmrUnforeseenEvents) return 'S'
    return 'auto'
  }, [fareRules, showCfmrUnforeseenEvents])

  return (<ModalBase size={modalSize} data-testid="fare-family-select-modal">
    <ModalHeader
      title={title}
      subtitle={showCfmrUnforeseenEvents ? cfmrSubtitle : undefined}
      onCloseButtonClick={onCloseButtonClick}
      onBackButtonClick={fareRules ? onCloseButtonClick : undefined}
    />
    <ModalBody ref={modalRef}>
      <ModalContent>
        <div data-fare-family-type={isReturnType ? 'return' : 'one-way'}>
          {!fareRules && !showCfmrUnforeseenEvents && (
            <VerticalSpacer gap={24}>
              <VerticalSpacer gap={4}>
                {!isReturnType && <FareFamilySelectModalSingleRouteFareHeaderItem flight={journey} />}

                {isReturnType && departingJourney && (
                  <FareFamilySelectModalReturnFareHeaderItem flights={[departingJourney, journey]} />
                )}
              </VerticalSpacer>

              {error && <ErrorPane background="light-background">
                <VerticalSpacer gap={16}>
                  <Heading variant="heading5">
                    {intl.formatMessage({
                      id: 'DDY5BMNxXtZUlN2Tgji3',
                      defaultMessage: 'Oh no, something went wrong',
                      description: 'Error state heading when fare families fail to load',
                    })}
                  </Heading>
                  <BodyText variant="large">
                    {intl.formatMessage({
                      id: 'OT1h6sZVbDe-jeqPMvU9',
                      defaultMessage: 'We ran into a problem when trying to load the information. Please go back and try again or call our support team on the number below.',
                      description: 'Error notification displayed when fare families fail to load',
                    })}
                  </BodyText>
                  <HelpPane />
                </VerticalSpacer>
              </ErrorPane> }

              {!error && <VerticalSpacer gap={12}>
                <CardCarousel
                  width={isCroFlexibleFlightFaresEnabled ? rem(275) : rem(250)}
                  gap={16}
                  gutterStyle="overflow"
                  gutterSize={24}
                >
                  {fetching && <>
                    <FareFamilySelectFareSkeleton />
                    <FareFamilySelectFareSkeleton />
                    <FareFamilySelectFareSkeleton />
                  </>}
                  {sortedFamilies.map((fareFamily) => <FareFamilyFareOption
                    selected={isFareFamilyOptionSelected(fareFamily)}
                    key={fareFamily.id}
                    fareFamily={fareFamily}
                    features={featuresByFareFamily.get(fareFamily.id)}
                    onSelect={setFareFamilySelected}
                    onShowFareRules={onShowFareRules}
                    onShowMore={toggleShowAllFeatures}
                    onShowCfmrUnforeseenEvents={onShowCfmrUnforeseenEvents}
                    showMore={showAllFeatures}
                    relativePrice={relativePrice}
                    isReturnType={isReturnType}
                    journey={journey}
                    departingJourney={departingJourney}
                    departingFareFamily={departingFareFamily}
                    isStandalone={isStandalone}
                    isDomestic={isDomestic}
                    isCroFlexibleFlightFaresEnabled={containsProtectedFareFamily}
                    feederBudgetLabel={feederBudgetLabelByFareFamilyId?.get(fareFamily.id)}
                  />)}
                </CardCarousel>

                <FareFamilyDisclaimerContainer>
                  <FareFamilyModalDisclaimer />
                </FareFamilyDisclaimerContainer>
              </VerticalSpacer>}
            </VerticalSpacer>
          )}

          {fareRules && (
            <FlightFareRulesModalContent
              urls={[fareRules]}
            />
          )}

          {insuranceTripStartDate && showCfmrUnforeseenEvents && (
            <CFMRUnforeseenEventsModalContent
              occupants={occupancies}
              destinationCountry={insuranceDestinationCountry}
              departingDate={insuranceTripStartDate}
              arrivalDate={insuranceTripEndDate}
              cfmrUnforeseenEventsCost={cfmrUnforeseenEventsCost}
              insuranceQuotes={insuranceQuotes}
            />
          )}
        </div>
      </ModalContent>
    </ModalBody>
    {!showCfmrUnforeseenEvents && (
      <ModalFooter
        primaryActionProps={primaryModalProps}
        secondaryActionProps={secondaryModalProps}
      />
    )}
  </ModalBase>)
}

export default FareFamilySelectModal
